import React, { useState, useEffect } from "react";
import { supabase } from "./config/supabase";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  sanitizeError,
  isValidPassword,
  getPasswordValidationError,
  globalRateLimiter,
} from "./utils/security";

interface RegistrationForm {
  password: string;
  confirmPassword: string;
  treatmentType: "self" | "child";
  name: string;
  surname: string;
}

const initialFormState: RegistrationForm = {
  password: "",
  confirmPassword: "",
  treatmentType: "self",
  name: "",
  surname: "",
};

const RegistrationPage: React.FC = () => {
  const [form, setForm] = useState<RegistrationForm>(initialFormState);
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [invitationValid, setInvitationValid] = useState<boolean>(false);
  const [invitationEmail, setInvitationEmail] = useState<string>("");
  const [validatingInvitation, setValidatingInvitation] =
    useState<boolean>(true);
  const [oauthLoading, setOauthLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role =
    (searchParams.get("role") as "doctor" | "patient" | null) || null;

  // Helper function to process name input (capitalize and remove numbers)
  const processNameInput = (value: string): string => {
    // Remove any numbers and special characters except spaces, hyphens, and apostrophes
    const cleaned = value.replace(/[^A-Za-z\s\-']/g, "");
    // Capitalize first letter of each word
    return cleaned
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Input validation using security utilities
  const validatePassword = isValidPassword;

  // Handle OAuth callback after Google/Apple registration
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const oauthParam = searchParams.get("oauth");
      const token = searchParams.get("token");

      if ((oauthParam === "google" || oauthParam === "apple") && token) {
        // Check if user is authenticated after OAuth
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          try {
            // Validate invitation token and get email
            const { data: validationData, error: validationError } =
              await supabase.functions.invoke(
                "validate-registration-invitation",
                {
                  body: { token },
                },
              );

            if (validationError || !validationData?.valid) {
              setStatus("Invalid or expired invitation token.");
              setOauthLoading(false);
              return;
            }

            const invitationEmail = validationData.email?.toLowerCase();
            const oauthEmail = session.user.email?.toLowerCase();

            // Verify OAuth email matches invitation email
            if (invitationEmail !== oauthEmail) {
              setStatus(
                `Email mismatch. The invitation is for ${invitationEmail}, but you signed in with ${oauthEmail}.`,
              );
              setOauthLoading(false);
              return;
            }

            // Extract name from OAuth profile (Google or Apple)
            const userMetadata = session.user.user_metadata;
            const fullName =
              userMetadata?.full_name || userMetadata?.name || "";
            const nameParts = fullName.split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            // If we have name from OAuth provider, complete registration
            if (firstName && lastName) {
              // Create/update user_data entry
              const { error: userDataError } = await supabase
                .from("user_data")
                .upsert(
                  {
                    user_id: session.user.id,
                    email: oauthEmail,
                    name: firstName.trim(),
                    surname: lastName.trim(),
                    account_type: role === "doctor" ? "doctor" : "patient",
                    treatment_type: role === "doctor" ? null : "self",
                  },
                  {
                    onConflict: "user_id",
                    ignoreDuplicates: false,
                  },
                );

              if (userDataError) throw userDataError;

              // Try to mark invitation as used (may fail if RPC requires admin, but that's okay)
              try {
                await supabase.rpc("mark_invitation_used", {
                  p_token: token,
                });
              } catch {
                // Non-critical - invitation marking may require admin privileges
              }

              setStatus("Registration successful! Redirecting to dashboard...");
              setOauthLoading(false);

              // Redirect to dashboard after 2 seconds
              setTimeout(() => {
                navigate("/dashboard");
              }, 2000);
            } else {
              // Name not available from provider, ask user to complete profile
              setStatus("Please complete your profile information below.");
              setOauthLoading(false);
            }
          } catch (err: any) {
            const safeError = sanitizeError(err);
            setStatus(
              `Registration error: ${safeError}. Please try again or complete the form below.`,
            );
            setOauthLoading(false);
          }
        } else {
          setOauthLoading(false);
        }
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, role]);

  // Validate invitation token on component mount
  useEffect(() => {
    const validateInvitation = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus(
          "Invalid registration link. Please contact us for a new invitation.",
        );
        setValidatingInvitation(false);
        return;
      }

      try {
        // Call Supabase function to validate the invitation token
        const response = await supabase.functions.invoke(
          "validate-registration-invitation",
          {
            body: { token },
          },
        );

        if (response.error) throw response.error;

        if (response.data && response.data.valid) {
          setInvitationValid(true);
          setInvitationEmail(response.data.email);
        } else {
          setStatus(
            "This invitation link is invalid or has expired. Please contact us for a new invitation.",
          );
        }
      } catch (err: any) {
        console.error("Invitation validation error:", err);
        setStatus(
          "Failed to validate invitation. Please contact us for assistance.",
        );
      } finally {
        setValidatingInvitation(false);
      }
    };

    validateInvitation();
  }, [searchParams]);

  // Google OAuth registration handler
  const handleGoogleRegister = async () => {
    setOauthLoading(true);
    setStatus("");
    try {
      const token = searchParams.get("token");
      if (!token) {
        setStatus(
          "Invalid registration link. Please contact us for a new invitation.",
        );
        setOauthLoading(false);
        return;
      }

      // Determine redirect URL based on environment
      let redirectUrl: string;
      const hostname = window.location.hostname;

      if (hostname === "amblyfy.com" || hostname === "www.amblyfy.com") {
        redirectUrl = `https://amblyfy.com/register?token=${token}&oauth=google`;
      } else if (hostname === "localhost" || hostname === "127.0.0.1") {
        // For localhost development
        redirectUrl = `http://${hostname}:${window.location.port}/register?token=${token}&oauth=google`;
      } else {
        // For other development/staging environments
        redirectUrl = `${window.location.origin}/register?token=${token}&oauth=google`;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      if (error) throw error;
      // The redirect will happen automatically
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setStatus(safeError);
      setOauthLoading(false);
    }
  };

  // Apple OAuth registration handler
  const handleAppleRegister = async () => {
    setOauthLoading(true);
    setStatus("");
    try {
      const token = searchParams.get("token");
      if (!token) {
        setStatus(
          "Invalid registration link. Please contact us for a new invitation.",
        );
        setOauthLoading(false);
        return;
      }

      let redirectUrl: string;
      const hostname = window.location.hostname;

      if (hostname === "amblyfy.com" || hostname === "www.amblyfy.com") {
        redirectUrl = `https://amblyfy.com/register?token=${token}&oauth=apple`;
      } else if (hostname === "localhost" || hostname === "127.0.0.1") {
        redirectUrl = `http://${hostname}:${window.location.port}/register?token=${token}&oauth=apple`;
      } else {
        redirectUrl = `${window.location.origin}/register?token=${token}&oauth=apple`;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setStatus(safeError);
      setOauthLoading(false);
    }
  };

  // Registration handler
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    // Rate limiting check
    if (!globalRateLimiter.canAttempt("register", 5, 10 * 60 * 1000)) {
      setStatus(
        "Too many registration attempts. Please try again in 10 minutes.",
      );
      setLoading(false);
      return;
    }

    // Validate inputs
    if (!form.name.trim() || !form.surname.trim()) {
      setStatus("Please enter your name and surname.");
      setLoading(false);
      return;
    }

    // Treatment type is always "self" by default - can be changed in settings later

    if (!validatePassword(form.password)) {
      const passwordError = getPasswordValidationError(form.password);
      setStatus(passwordError || "Password does not meet requirements.");
      setLoading(false);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setStatus("Passwords do not match.");
      setLoading(false);
      return;
    }

    const token = searchParams.get("token");

    try {
      // Call Supabase function to complete registration
      const { error } = await supabase.functions.invoke(
        "complete-registration",
        {
          body: {
            token,
            name: form.name.trim(),
            surname: form.surname.trim(),
            password: form.password,
            treatmentType: "self", // Always default to "self" - can be changed in settings
            role: role === "doctor" ? "doctor" : "patient",
          },
        },
      );

      if (error) throw error;

      // Reset rate limiter on success
      globalRateLimiter.reset("register");

      setStatus(
        "Registration successful! Please check your email to confirm your account.",
      );
      setForm(initialFormState);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setStatus(safeError);
    } finally {
      setLoading(false);
    }
  };

  if (validatingInvitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center py-6 px-2 sm:px-6 lg:px-8">
        <div className="w-full max-w-md sm:max-w-lg bg-white rounded-2xl shadow-2xl p-4 sm:p-8 space-y-8 mx-auto">
          <div className="text-center">
            <a href="/" className="inline-block mb-6">
              <img
                src="/assets/Amblify_logo_zilsfix.png"
                alt="Amblyfy"
                className="h-12 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin h-8 w-8 text-brand-dark-blue"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-3 text-brand-dark-blue font-medium">
                Validating invitation...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invitationValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center py-6 px-2 sm:px-6 lg:px-8">
        <div className="w-full max-w-md sm:max-w-lg bg-white rounded-2xl shadow-2xl p-4 sm:p-8 space-y-8 mx-auto">
          <div className="text-center">
            <a href="/" className="inline-block mb-6">
              <img
                src="/assets/Amblify_logo_zilsfix.png"
                alt="Amblyfy"
                className="h-12 mx-auto"
              />
            </a>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark-blue mb-4">
              Invalid Invitation
            </h2>
            <div className="bg-red-50 text-red-800 border border-red-200 p-4 rounded-lg">
              {status}
            </div>
            <div className="mt-6">
              <a
                href="/#contact"
                className="bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-block"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center py-6 px-2 sm:px-6 lg:px-8">
      <div className="w-full max-w-md sm:max-w-lg bg-white rounded-2xl shadow-2xl p-4 sm:p-8 space-y-8 mx-auto">
        {/* Header */}
        <div className="text-center">
          <a href="/" className="inline-block mb-6">
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-12 mx-auto"
            />
          </a>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark-blue mb-2">
            Complete Your Registration
          </h2>
          <p className="text-gray-700 opacity-90 text-sm sm:text-base">
            Welcome! Please complete your account setup for {invitationEmail}
          </p>
        </div>

        {/* Registration Form */}
        <div className="bg-gray-50 rounded-lg shadow p-4 sm:p-8">
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Enter your first name"
                  maxLength={50}
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: processNameInput(e.target.value) })
                  }
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                />
              </div>
              <div>
                <label
                  htmlFor="surname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Last Name
                </label>
                <input
                  id="surname"
                  type="text"
                  placeholder="Enter your last name"
                  maxLength={50}
                  value={form.surname}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      surname: processNameInput(e.target.value),
                    })
                  }
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                placeholder="Your password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, and one number."
                maxLength={128}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
              />
              <div className="text-xs text-gray-500 mt-1 text-left">
                Your password must be at least 8 characters long and include at
                least one uppercase letter, one lowercase letter, and one
                number.
              </div>
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                maxLength={128}
                value={form.confirmPassword}
                onChange={(e) =>
                  setForm({ ...form, confirmPassword: e.target.value })
                }
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
              />
            </div>

            <button
              type="submit"
              disabled={loading || oauthLoading}
              className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating account...
                </div>
              ) : (
                "Complete Registration"
              )}
            </button>
            {status && (
              <div
                className={`p-3 rounded-lg text-sm font-medium mt-2 ${
                  status.startsWith("Registration successful") ||
                  status.startsWith("Please complete")
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {status}
              </div>
            )}
          </form>

          {/* OAuth Section */}
          <div className="space-y-3 mt-5">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="flex-shrink mx-4 text-gray-500 text-sm">OR</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleRegister}
              disabled={loading || oauthLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg shadow-sm hover:shadow-md hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {oauthLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-gray-700"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleAppleRegister}
              disabled={loading || oauthLoading}
              className="w-full flex items-center justify-center gap-3 bg-black text-white font-semibold py-3 px-4 rounded-lg shadow-sm hover:shadow-md hover:bg-gray-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {oauthLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span>Continue with Apple</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="text-center">
          <a
            href="/"
            className="text-brand-dark-blue hover:text-brand-cyan transition-colors text-sm font-medium"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
